import type { ItemWithInstanceCounts, ConsolidatedItem, ItemUnit } from '../types';
import { convertQuantity, canConvertUnits } from './index';

/** Stable-partition items so active (quantity > 0) come before depleted. */
function activeFirst<T extends ItemWithInstanceCounts>(items: T[]): T[] {
  return [...items].sort((a, b) => (b.activeQuantity > 0 ? 1 : 0) - (a.activeQuantity > 0 ? 1 : 0));
}

/**
 * Type helper for items that may have product group info (added by API Phase 0.2).
 * The productGroup field may be on the item directly or not yet in the type.
 */
interface ItemWithProductGroup extends ItemWithInstanceCounts {
  productGroup?: {
    id: string;
    name: string;
    icon: string | null;
    category: string | null;
    targetQuantity?: number | null;
    targetUnit?: string | null;
  } | null;
}

/**
 * Group-aware consolidation of items for inventory display.
 *
 * Grouping priority:
 * 1. Items whose product has a productGroup → group by productGroup.id
 * 2. Items with a productId but no group → group by productId
 * 3. Items with only customName → shown individually
 *
 * Ordering: groups first (sorted by name), then products (sorted by name), then custom items.
 */
export function consolidateItems(items: ItemWithInstanceCounts[]): ConsolidatedItem[] {
  const typedItems = items as ItemWithProductGroup[];

  // Buckets: group-keyed, product-keyed, custom (per item)
  const groupMap = new Map<string, ItemWithProductGroup[]>();
  const productMap = new Map<string, ItemWithProductGroup[]>();
  const customItems: ItemWithProductGroup[] = [];

  for (const item of typedItems) {
    if (item.productGroup?.id) {
      const existing = groupMap.get(item.productGroup.id) ?? [];
      existing.push(item);
      groupMap.set(item.productGroup.id, existing);
    } else if (item.productId) {
      const existing = productMap.get(item.productId) ?? [];
      existing.push(item);
      productMap.set(item.productId, existing);
    } else {
      customItems.push(item);
    }
  }

  const results: ConsolidatedItem[] = [];

  // --- Group-level entries ---
  for (const [, groupItems] of groupMap) {
    // All items in this group share the same productGroup — use the first one
    const pg = groupItems[0].productGroup!;

    const totalActiveQuantity = groupItems.reduce((sum, i) => sum + (i.activeQuantity ?? 0), 0);
    const totalActiveInstances = groupItems.reduce(
      (sum, i) => sum + (i.activeInstanceCount ?? 0),
      0
    );

    // Distinct product IDs in this group
    const distinctProducts = new Set(groupItems.map((i) => i.productId).filter(Boolean));
    const productCount = distinctProducts.size || 1;

    // Target comparison — convert to targetUnit when possible
    const targetQuantity = pg.targetQuantity ?? null;
    const targetUnit = pg.targetUnit ?? null;

    let isBelowTarget = false;
    if (targetQuantity != null) {
      if (targetUnit) {
        // Sum quantities converted to targetUnit (skip unconvertible items)
        const convertedTotal = groupItems.reduce((sum, item) => {
          const itemUnit = item.unit as ItemUnit;
          const tUnit = targetUnit as ItemUnit;
          if (!canConvertUnits(itemUnit, tUnit)) return sum;
          const converted = convertQuantity(item.activeQuantity, itemUnit, tUnit);
          return sum + (converted ?? 0);
        }, 0);
        isBelowTarget = convertedTotal < targetQuantity;
      } else {
        isBelowTarget = totalActiveQuantity < targetQuantity;
      }
    }

    const isDepleted = totalActiveQuantity === 0 && targetQuantity == null;

    results.push({
      groupId: pg.id,
      productId: null,
      displayName: pg.name,
      displayIcon: pg.icon,
      displayImage: null,
      displayBrand: null,
      category: pg.category,
      targetQuantity,
      targetUnit,
      items: activeFirst(groupItems),
      productCount,
      totalActiveQuantity,
      totalActiveInstances,
      isBelowTarget,
      isDepleted,
    });
  }

  // Sort group entries by name
  results.sort((a, b) => a.displayName.localeCompare(b.displayName));

  const productResults: ConsolidatedItem[] = [];

  // --- Product-level entries ---
  for (const [productId, prodItems] of productMap) {
    const first = prodItems[0];
    const product = first.product;

    const totalActiveQuantity = prodItems.reduce((sum, i) => sum + (i.activeQuantity ?? 0), 0);
    const totalActiveInstances = prodItems.reduce(
      (sum, i) => sum + (i.activeInstanceCount ?? 0),
      0
    );

    // Item-level target: use the first non-null target found across items for this product
    const targetItem = prodItems.find((i) => i.targetQuantity != null);
    const targetQuantity = targetItem?.targetQuantity ?? null;
    const targetUnit = targetItem?.targetUnit ?? null;

    let isBelowTarget = false;
    if (targetQuantity != null) {
      if (targetUnit) {
        const convertedTotal = prodItems.reduce((sum, item) => {
          const itemUnit = item.unit as ItemUnit;
          const tUnit = targetUnit as ItemUnit;
          if (!canConvertUnits(itemUnit, tUnit)) return sum;
          const converted = convertQuantity(item.activeQuantity, itemUnit, tUnit);
          return sum + (converted ?? 0);
        }, 0);
        isBelowTarget = convertedTotal < targetQuantity;
      } else {
        isBelowTarget = totalActiveQuantity < targetQuantity;
      }
    }

    const isDepleted = totalActiveQuantity === 0 && targetQuantity == null;

    productResults.push({
      groupId: null,
      productId,
      displayName: product?.name ?? first.customName ?? '',
      displayIcon: null,
      displayImage: product?.imageUrl ?? null,
      displayBrand: product?.brand ?? null,
      category: product?.category ?? null,
      targetQuantity,
      targetUnit,
      items: activeFirst(prodItems),
      productCount: 1,
      totalActiveQuantity,
      totalActiveInstances,
      isBelowTarget,
      isDepleted,
    });
  }

  // Sort product entries by name
  productResults.sort((a, b) => a.displayName.localeCompare(b.displayName));

  const customResults: ConsolidatedItem[] = [];

  // --- Custom-name entries (individual) ---
  for (const item of customItems) {
    const targetQuantity = item.targetQuantity ?? null;
    const targetUnit = item.targetUnit ?? null;

    let isBelowTarget = false;
    if (targetQuantity != null) {
      isBelowTarget = item.activeQuantity < targetQuantity;
    }

    const isDepleted =
      item.activeQuantity === 0 && (targetQuantity == null || targetQuantity === 0);

    customResults.push({
      groupId: null,
      productId: null,
      displayName: item.customName ?? '',
      displayIcon: null,
      displayImage: null,
      displayBrand: null,
      category: null,
      targetQuantity,
      targetUnit,
      items: [item],
      productCount: 1,
      totalActiveQuantity: item.activeQuantity,
      totalActiveInstances: item.activeInstanceCount,
      isBelowTarget,
      isDepleted,
    });
  }

  return [...results, ...productResults, ...customResults];
}

/**
 * Partitions a list of consolidated items into visibility buckets for UI rendering.
 *
 * - `needsRestocking`: items with isBelowTarget === true
 * - `visible`: items that are neither depleted nor below target
 * - `depleted`: items with isDepleted === true
 */
export function partitionByVisibility(consolidated: ConsolidatedItem[]): {
  visible: ConsolidatedItem[];
  depleted: ConsolidatedItem[];
  needsRestocking: ConsolidatedItem[];
} {
  const visible: ConsolidatedItem[] = [];
  const depleted: ConsolidatedItem[] = [];
  const needsRestocking: ConsolidatedItem[] = [];

  for (const item of consolidated) {
    if (item.isBelowTarget) {
      needsRestocking.push(item);
    } else if (item.isDepleted) {
      depleted.push(item);
    } else {
      visible.push(item);
    }
  }

  return { visible, depleted, needsRestocking };
}
